<template>
  <div>
    <!-- 테이블 공통 하단스크롤 제거 -->
    <div class="overflow-y-auto overflow-x-hidden" :class="tableClass">
      <table class="w-full overflow-hidden rounded-tl-md rounded-tr-md">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.value"
              class="px-[1.48rem] py-2 text-white bg-slate-800 text-sm leading-7 -tracking-[0.2px] text-xs"
              :class="col.headerClass"
              :style="{ width: col.width }"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody class="overflow-scroll">
          <tr v-if="!tableRows.length">
            <td
              :colspan="columns.length"
              class="h-10 text-center bg-[#f5f5f5] text-gray-05 -tracking-[0.2px] text-[11px] leading-6 text-xs"
            >{{ noDataLabel }}</td>
          </tr>
          <template v-else>
            <tr
              v-for="row in tableRows"
              :key="row"
              class="odd:bg-[#f5f5f5] even:bg-[#ECECEC] hover:bg-gray-200 cursor-pointer"
              :class="{ 'selected-row': selectedRow?.notification_id === row.notification_id }"
              @click="emitRowClick(row)"
            >
              <td
                v-for="col in columns"
                :key="col.value"
                class="h-10 text-gray-05 -tracking-[0.2px] text-[11px] leading-6"
                :class="col.label === '제목' ? 'text-left' : 'text-center'"
              >
                <slot :name="`td-${col.value}`" v-bind="{ row, col }">
                  {{ row[col.value] }}
                </slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 페이지네이션 -->
    <ul class="pt-4" v-if="pagination.page && tableRows.length">
      <li class="flex items-center justify-center gap-4">
        <button
          class="inline-flex items-center justify-center w-8 h-8 border rounded-full"
          @click="prevPageSet"
          :disabled="pagination.page === 1"
          :class="{ 'opacity-50 pointer-events-none': pagination.page === 1 }"
        >
          <img :src="paginationIcon" alt="prev" class="rotate-180" />
        </button>
        <div class="inline-flex gap-4">
          <button
            v-for="page in pages"
            :key="page"
            class="inline-flex items-center justify-center w-8 h-8 border rounded-full"
            :class="page === pagination.page ? 'font-bold !border-violet-500 text-violet-600' : 'border-black'"
            @click="() => changePage(page)"
          >{{ page }}</button>
        </div>
        <button
          class="inline-flex items-center justify-center w-8 h-8 border rounded-full"
          :disabled="pagination.page === pagination.lastPage"
          :class="{ 'opacity-50 pointer-events-none': pagination.page === pagination.lastPage }"
          @click="nextPageSet"
        >
          <img :src="paginationIcon" alt="next" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import paginationIcon from '@/assets/pagination.svg'
import { computed } from 'vue'

// props 정의
const props = defineProps({
  columns: { type: Array, default: () => [] },
  rows: { type: Array, default: () => [] },
  noDataLabel: { type: String, default: '데이터가 없습니다.' },
  pagination: { type: Object, default: () => ({ page: 0, perPage: 10, lastPage: 1 }) },
  tableClass: { type: String },
  selectedRow: { type: Object, default: null }  // ✅ 선택된 row
})

// emits 정의
const emits = defineEmits(['update:page', 'row-click'])

function emitRowClick(row) {
  emits('row-click', row)
}

// 페이지 계산
const PAGINATION_PER_PAGE = 10

const totalPages = computed(() => (
  props.rows.length === 0 ? 0 : Math.ceil(props.rows.length / props.pagination.perPage)
))

const currentPageSet = computed(() => (
  totalPages.value === 0 ? 0 : Math.ceil(props.pagination.page / PAGINATION_PER_PAGE)
))

const pages = computed(() => {
  if (totalPages.value === 0) return []
  const start = (currentPageSet.value - 1) * PAGINATION_PER_PAGE + 1
  const end = Math.min(start + 9, totalPages.value)
  return Array.from({ length: end - start + 1 }, (_, idx) => start + idx)
})

const tableRows = computed(() => {
  if (!props.pagination.page) return props.rows
  const startIndex = (props.pagination.page - 1) * props.pagination.perPage
  return props.rows.slice(startIndex, startIndex + props.pagination.perPage)
})

function changePage(page) {
  if (page > 0 && page <= totalPages.value) {
    emits('update:page', page)
  }
}

function nextPageSet() {
  const newPage = Math.min(props.pagination.page + PAGINATION_PER_PAGE, totalPages.value)
  emits('update:page', newPage)
}

function prevPageSet() {
  const newPage = Math.max(props.pagination.page - PAGINATION_PER_PAGE, 1)
  emits('update:page', newPage)
}
</script>

<style lang="scss" scoped>
.selected-row {
  background-color: #ede9fe !important; /* violet-100 하이라이트 */
  font-weight: bold;
}
</style>
